import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Star, Check, X, Trash2, MessageSquare } from 'lucide-react';
import api from '../../utils/api';

function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetReviews(token, page, filter);
      setReviews(data.reviews || []);
      setTotalPages(data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (reviewId, approve) => {
    try {
      await api.adminModerateReview(reviewId, approve, token);
      toast.success(approve ? 'Review approved!' : 'Review rejected');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to moderate review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await api.adminDeleteReview(reviewId, token);
      toast.success('Review deleted!');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const StarRating = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  if (loading && reviews.length === 0) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Reviews</h1>
            <p className="text-muted-foreground">Manage customer reviews and ratings</p>
          </div>
          <div className="flex gap-2">
            {['all', 'approved', 'pending'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setFilter(status);
                  setPage(1);
                }}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {reviews.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No reviews yet</h3>
            <p className="text-muted-foreground">Customer reviews will appear here</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <Card key={review.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <StarRating rating={review.rating} />
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        review.is_approved 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {review.is_approved ? 'Approved' : 'Pending'}
                      </span>
                      {review.is_verified_purchase && (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-1">
                      <strong>{review.product_title}</strong>
                    </p>
                    
                    {review.title && (
                      <h4 className="font-medium">{review.title}</h4>
                    )}
                    
                    {review.comment && (
                      <p className="text-sm text-muted-foreground mt-1">{review.comment}</p>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      By {review.user_name} • {new Date(review.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    {!review.is_approved && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleModerate(review.id, true)}
                        title="Approve"
                      >
                        <Check className="w-4 h-4 text-green-500" />
                      </Button>
                    )}
                    {review.is_approved && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleModerate(review.id, false)}
                        title="Reject"
                      >
                        <X className="w-4 h-4 text-orange-500" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDelete(review.id)}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminReviewsPage;
